import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import MLMUserReportsModal from '../mlm/MLMUserReportsModal';
import MLMUserLeadsModal from '../mlm/MLMUserLeadsModal';

/**
 * MLM Side Panel Component
 * Displays selected node's performance data using PrimeReact + PrimeFlex only
 * Follows Diamond template patterns
 *
 * @param {object} nodeData - Node performance data
 * @param {boolean} loading - Loading state
 * @param {string} error - Error message
 * @param {string} accessType - Access type for reports: 'usermlm' (default) or 'orguser'
 */
const MLMSidePanel = ({ nodeData, loading, error, accessType = 'usermlm' }) => {
    const [showReportsModal, setShowReportsModal] = useState(false);
    const [reportType, setReportType] = useState('my'); // 'my', 'network', or 'total'
    const [showLeadsModal, setShowLeadsModal] = useState(false);
    const [leadType, setLeadType] = useState('total'); // 'my', 'network', or 'total'
    // Loading State
    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                <div className="text-center">
                    <ProgressSpinner />
                    <p className="mt-3 text-600">Loading performance data...</p>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <Card className="mb-3">
                <Message severity="error" text={error} className="w-full" />
            </Card>
        );
    }

    // Empty State
    if (!nodeData) {
        return (
            <Card className="mb-3">
                <Message
                    severity="info"
                    text="Click on a node in the tree to view performance data"
                    className="w-full"
                />
            </Card>
        );
    }

    const { user, performance } = nodeData;

    return (
        <div>
            {/* User Header Card - Responsive */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <div className="flex align-items-center gap-2 md:gap-3">
                    <Avatar
                        label={user.name.charAt(0).toUpperCase()}
                        size="large"
                        shape="circle"
                        className="flex-shrink-0"
                        style={{
                            backgroundColor: '#4F46E5',
                            color: '#ffffff',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                        }}
                    />
                    <div className="flex-1 overflow-hidden">
                        <h3 className="m-0 mb-1 md:mb-2 text-base md:text-xl font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{user.name}</h3>
                        <p className="m-0 mb-1 md:mb-2 text-xs md:text-sm text-600 white-space-nowrap overflow-hidden text-overflow-ellipsis">{user.email}</p>
                        <Tag
                            icon="pi pi-calendar"
                            value={`Joined: ${user.joined_date}`}
                            severity="secondary"
                            className="text-xs"
                        />
                    </div>
                </div>
            </Card>

            {/* Performance Stats Cards - Responsive */}
            <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">📊 Performance Overview</h4>
            <div className="grid mb-2 md:mb-3">
                <div className="col-6">
                    <Card className="h-full p-2 md:p-3">
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-users text-2xl md:text-4xl" style={{ color: '#4F46E5' }}></i>
                            <div className="flex-1">
                                <div className="text-xl md:text-4xl font-bold text-900">{performance.network_size}</div>
                                <div className="text-xs text-500 font-medium uppercase">Network Size</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card
                        className="h-full p-2 md:p-3 cursor-pointer hover:surface-100 transition-colors transition-duration-200"
                        onClick={() => {
                            setReportType('total');
                            setShowReportsModal(true);
                        }}
                    >
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-file text-2xl md:text-4xl" style={{ color: '#10B981' }}></i>
                            <div className="flex-1">
                                <div className="flex align-items-center gap-1 md:gap-2">
                                    <span className="text-xl md:text-4xl font-bold text-900">{performance.total_reports}</span>
                                    <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                                </div>
                                <div className="text-xs text-500 font-medium uppercase">Total Reports</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card className="h-full p-2 md:p-3">
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-user-plus text-2xl md:text-4xl" style={{ color: '#F59E0B' }}></i>
                            <div className="flex-1">
                                <div className="text-xl md:text-4xl font-bold text-900">{performance.direct_referrals}</div>
                                <div className="text-xs text-500 font-medium uppercase">Direct Referrals</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card className="h-full p-2 md:p-3">
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-sitemap text-2xl md:text-4xl" style={{ color: '#8B5CF6' }}></i>
                            <div className="flex-1">
                                <div className="text-xl md:text-4xl font-bold text-900">{performance.network_depth}</div>
                                <div className="text-xs text-500 font-medium uppercase">Network Depth</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card
                        className="h-full p-2 md:p-3 cursor-pointer hover:surface-100 transition-colors transition-duration-200"
                        onClick={() => setShowLeadsModal(true)}
                    >
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-id-card text-2xl md:text-4xl" style={{ color: '#EC4899' }}></i>
                            <div className="flex-1">
                                <div className="flex align-items-center gap-1 md:gap-2">
                                    <span className="text-xl md:text-4xl font-bold text-900">{performance.total_leads || 0}</span>
                                    <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                                </div>
                                <div className="text-xs text-500 font-medium uppercase">Total Leads</div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Activity Breakdown Card - Reports - Responsive */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">📈 Reports Breakdown</h4>

                <div
                    className="flex justify-content-between align-items-center py-2 cursor-pointer hover:surface-100 border-round p-2 transition-colors transition-duration-200"
                    onClick={() => {
                        setReportType('my');
                        setShowReportsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view my reports"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-600"></i>
                        <span className="text-600 font-medium text-sm md:text-base">My Reports</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-base md:text-xl font-bold text-900">{performance.my_reports}</span>
                        <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                    </div>
                </div>

                <div
                    className="flex justify-content-between align-items-center py-2 cursor-pointer hover:surface-100 border-round p-2 transition-colors transition-duration-200"
                    onClick={() => {
                        setReportType('network');
                        setShowReportsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view network reports"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-users text-600"></i>
                        <span className="text-600 font-medium text-sm md:text-base">Network Reports</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-base md:text-xl font-bold text-900">{performance.network_reports}</span>
                        <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                    </div>
                </div>

                <Divider />

                <div
                    className="flex justify-content-between align-items-center pt-2 cursor-pointer hover:surface-100 border-round p-2 transition-colors transition-duration-200"
                    onClick={() => {
                        setReportType('total');
                        setShowReportsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view all reports"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-chart-bar text-primary"></i>
                        <span className="text-900 font-bold text-sm md:text-base">Total Reports</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl md:text-2xl font-bold text-primary">{performance.total_reports}</span>
                        <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                    </div>
                </div>
            </Card>

            {/* Activity Breakdown Card - Leads - Responsive */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">👥 Leads Breakdown</h4>

                <div
                    className="flex justify-content-between align-items-center py-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setLeadType('my');
                        setShowLeadsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view my leads"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-600"></i>
                        <span className="text-600 font-medium text-sm md:text-base">My Leads</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-base md:text-xl font-bold text-900">{performance.my_leads || 0}</span>
                        <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                    </div>
                </div>

                <div
                    className="flex justify-content-between align-items-center py-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setLeadType('network');
                        setShowLeadsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view network leads"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-users text-600"></i>
                        <span className="text-600 font-medium text-sm md:text-base">Network Leads</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-base md:text-xl font-bold text-900">{performance.network_leads || 0}</span>
                        <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                    </div>
                </div>

                <Divider />

                <div
                    className="flex justify-content-between align-items-center pt-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setLeadType('total');
                        setShowLeadsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view all leads"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-id-card" style={{ color: '#EC4899' }}></i>
                        <span className="text-900 font-bold text-sm md:text-base">Total Leads</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl md:text-2xl font-bold" style={{ color: '#EC4899' }}>{performance.total_leads || 0}</span>
                        <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                    </div>
                </div>
            </Card>

            {/* MLM User Reports Modal */}
            <MLMUserReportsModal
                visible={showReportsModal}
                onHide={() => setShowReportsModal(false)}
                userId={user.id}
                userName={user.name}
                reportType={reportType}
                accessType={accessType}
            />

            {/* MLM User Leads Modal */}
            <MLMUserLeadsModal
                visible={showLeadsModal}
                onHide={() => setShowLeadsModal(false)}
                userId={user.id}
                userName={user.name}
                leadType={leadType}
                accessType={accessType}
            />
        </div>
    );
};

export default MLMSidePanel;
